import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { useContext, useEffect, useRef, useState } from "react";
import {
  NEW_CUSTOM_STRATEGY_VALUE,
  ORDER_TYPE_TO_TOOLTIP,
} from "@shared/features/tradingTerminal/common/constants";
import {
  CUSTOM_STRATEGIES_ITEM_NAME,
  USER_CONFIGURATION_STRATEGY_VALUE,
} from "@shared/features/configuration/constants";
import { toFixed } from "@shared/utils/numbers";
import {
  ConfigurationItem,
  LanguageNameSpaces,
  Option,
  OrderTypes,
} from "@shared/types";
import {
  getItemValueType,
  getName,
} from "@shared/features/configuration/utils";
import { useSharedCustomStrategies } from "@shared/features/configuration/hooks";
import { OverrideType } from "@shared/features/bots/BotForm/common/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useSharedSelectItemProps {
  orderType?: OrderTypes;
  disabled?: boolean;
  itemValue: any;
  handleItemChange: any;
  itemName: string;
  itemOptions: any[];
  itemChildren: ConfigurationItem[];
  itemShouldDisplay: boolean;
  isApp?: boolean;
}

const useSharedSelectItem = ({
  orderType,
  disabled,
  itemValue,
  handleItemChange,
  itemName,
  itemOptions,
  itemChildren,
  itemShouldDisplay,
  isApp,
}: useSharedSelectItemProps) => {
  const {
    ratioTargets,
    setRatioTargets,
    account,
    isSignal,
    subGroupIds,
    showNewCustomStrategy,
    precisionConfig,
  } = useContext(TradingTerminalContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);

  const buttonClickedRef = useRef(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [customStrategyModalVisible, setCustomStrategyModalVisible] =
    useState(false);
  const [targets, setTargets] = useState<number[]>();

  const { selectedCustomStrategy, customStrategies, customStrategiesItem } =
    useSharedCustomStrategies(itemValue, itemChildren);

  const isStrategyItem = itemName.includes("Strategy");
  const isNewCustomStrategy =
    isStrategyItem && orderType && showNewCustomStrategy[orderType];
  const tooltipProps =
    isStrategyItem && orderType ? ORDER_TYPE_TO_TOOLTIP[orderType] : undefined;

  const getOptions = () => {
    if (isStrategyItem) {
      let options = itemChildren?.filter(
        (child) => getName(child) !== CUSTOM_STRATEGIES_ITEM_NAME
      );
      if (selectedCustomStrategy) {
        options = [...options, selectedCustomStrategy];
      }
      return options?.map((option) => ({
        name: getName(option),
        value: Number(getItemValueType(option)),
      }));
    } else {
      return itemOptions;
    }
  };

  let options = getOptions();
  if (isSignal && isStrategyItem) {
    options.unshift({
      name: OverrideType.USER_CONFIGURATION,
      value: USER_CONFIGURATION_STRATEGY_VALUE,
    });
  }
  if (isNewCustomStrategy) {
    options = [
      ...options,
      {
        name: t("strategies.new_custom_strategy"),
        value: NEW_CUSTOM_STRATEGY_VALUE,
      },
    ];
  }

  useEffect(() => {
    if (disabled || (!itemShouldDisplay && options.length > 1)) {
      handleItemChange(null);
    } else if (options?.length) {
      const valueExistsInOptions = options?.find(
        (option: Option) => option.value === itemValue
      );
      if (!valueExistsInOptions) {
        handleItemChange(options?.[0]?.value);
      }
    }
  }, [options, disabled, itemShouldDisplay]);

  const strategyModalProps = {
    onlyCustomStrategies: true,
    isModalVisible: customStrategyModalVisible,
    closeModal: () => {
      setCustomStrategyModalVisible(false);
      buttonClickedRef.current = false;
    },
    useCustomStrategyEndpoints: true,
    orderType: orderType as OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT,
    targets: targets?.map((target) =>
      toFixed(target, precisionConfig?.percent)
    ),
    resetCustomStrategyTargets: () => {
      if (orderType) setRatioTargets(orderType, undefined);
    },
    strategyValuesModified: isNewCustomStrategy,
    accountId: account?.client_id,
    isSignal,
    subGroupIds,
    customStrategies,
    customStrategiesItem,
    hideEditAndRemove: true,
    precisionConfig,
  };

  const onSelectClick = () => {
    if (!buttonClickedRef.current) {
      setIsSelectOpen(!isSelectOpen);
    }
  };

  const onSaveClick = () => {
    setIsSelectOpen(false);
    buttonClickedRef.current = true;
    setTargets(orderType ? ratioTargets[orderType] : undefined);
    if (isApp) {
      setTimeout(() => setCustomStrategyModalVisible(true), 500);
    } else {
      setCustomStrategyModalVisible(true);
    }
  };

  const onCustomStrategiesClick = () => {
    setIsSelectOpen(false);
    setCustomStrategyModalVisible(true);
    setTargets(undefined);
  };

  return {
    options,
    isSelectOpen,
    onSelectClick,
    isStrategyItem,
    isNewCustomStrategy,
    strategyModalProps,
    tooltipProps,
    onSaveClick,
    onCustomStrategiesClick,
  };
};

export default useSharedSelectItem;
